import { useState, useRef, useEffect, useCallback } from "react";

export const ANIMATION_DURATION = 300;
export const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

type Phase = "mounting" | "open" | "closing";

export function useSlideAnimation(onClose: () => void) {
  const [phase, setPhaseState] = useState<Phase>("mounting");
  const phaseRef = useRef<Phase>("mounting");
  const onCloseRef = useRef(onClose);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  onCloseRef.current = onClose;

  function setPhase(p: Phase) {
    phaseRef.current = p;
    setPhaseState(p);
  }

  const prefersReducedMotion = useRef(
    typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  ).current;

  useEffect(function triggerEnterAnimation() {
    if (prefersReducedMotion) {
      setPhase("open");
      return;
    }
    let innerRafId: number;
    const outerRafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        setPhase("open");
      });
    });
    return () => {
      cancelAnimationFrame(outerRafId);
      cancelAnimationFrame(innerRafId);
    };
  }, []);

  const handleClose = useCallback(function handleClose() {
    if (phaseRef.current !== "open") return;
    if (prefersReducedMotion) {
      onCloseRef.current();
      return;
    }
    setPhase("closing");
    timerRef.current = setTimeout(() => onCloseRef.current(), ANIMATION_DURATION);
  }, []);

  const reopen = useCallback(function reopen() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    setPhase("open");
  }, []);

  useEffect(function cleanupTimer() {
    return () => clearTimeout(timerRef.current);
  }, []);

  return {
    phase,
    canAnimate: phase !== "mounting" && !prefersReducedMotion,
    handleClose,
    reopen,
  };
}
